import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  ArrowRight,
  Activity,
  RefreshCw,
  ClipboardCheck,
  FileText,
  MapPin,
  Building2
} from 'lucide-react';
import { workOrderService } from '../../../services/workOrderService';
import { assetService } from '../../../services/assetService';
import { useCmmsAuth } from '../../../contexts/CmmsAuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import PermissionGuard from '../../../components/PermissionGuard';
import ComponentsDueWidget from '../../../components/cmms/ComponentsDueWidget';
import PMSchedulesDueWidget from '../../../components/cmms/PMSchedulesDueWidget';
import { useDateFormat } from '../../../hooks/useDateFormat';

const CMSSDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [workOrders, setWorkOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [error, setError] = useState(null);

  const { hasPermission, cmmsUser } = useCmmsAuth();
  const canCreateWorkOrders = hasPermission(PERMISSIONS.CREATE_WORK_ORDERS);
  const canCreateAssets = hasPermission(PERMISSIONS.CREATE_ASSETS);
  const { formatDate } = useDateFormat();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [woStats, allWorkOrders, allAssets] = await Promise.all([
        workOrderService.getStats(),
        workOrderService.getWorkOrders(),
        assetService.getAssets()
      ]);

      setStats(woStats || {});
      setWorkOrders(allWorkOrders || []);
      setAssets(allAssets || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate asset statistics
  const assetsByStatus = React.useMemo(() => {
    const statusCounts = {
      OPERATIONAL: 0,
      UNDER_MAINTENANCE: 0,
      DOWN: 0,
      IDLE: 0,
      RETIRED: 0
    };

    assets.forEach(asset => {
      if (statusCounts[asset.status] !== undefined) {
        statusCounts[asset.status]++;
      }
    });

    const total = assets.length || 1;
    return [
      { status: 'OPERATIONAL', labelKey: 'cmms.asset_operational', count: statusCounts.OPERATIONAL, percentage: Math.round((statusCounts.OPERATIONAL / total) * 100), color: 'bg-green-500' },
      { status: 'UNDER_MAINTENANCE', labelKey: 'cmms.asset_maintenance', count: statusCounts.UNDER_MAINTENANCE, percentage: Math.round((statusCounts.UNDER_MAINTENANCE / total) * 100), color: 'bg-yellow-500' },
      { status: 'DOWN', labelKey: 'cmms.asset_down', count: statusCounts.DOWN, percentage: Math.round((statusCounts.DOWN / total) * 100), color: 'bg-red-500' },
      { status: 'IDLE', labelKey: 'cmms.asset_idle', count: statusCounts.IDLE, percentage: Math.round((statusCounts.IDLE / total) * 100), color: 'bg-gray-500' },
    ];
  }, [assets]);

  // Get upcoming/active work orders (not completed)
  const upcomingWorkOrders = React.useMemo(() => {
    return workOrders
      .filter(wo => wo.status !== 'COMPLETED' && wo.status !== 'CANCELLED')
      .sort((a, b) => {
        // Sort by priority first, then by scheduled date
        const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
        const priorityDiff = (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(a.scheduledStart || 0) - new Date(b.scheduledStart || 0);
      })
      .slice(0, 5);
  }, [workOrders]);

  // Get pending review work orders
  const pendingReviewOrders = React.useMemo(() => {
    return workOrders.filter(wo => wo.status === 'PENDING_REVIEW');
  }, [workOrders]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ASSIGNED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'PENDING_REVIEW': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const statsCards = [
    {
      name: t('cmms.totalAssets'),
      value: assets.length.toString(),
      change: `${assetsByStatus[0]?.count || 0} ${t('cmms.asset_operational').toLowerCase()}`,
      trend: 'up',
      icon: Package,
      color: 'bg-blue-500',
      href: '/portal/cmms/assets'
    },
    {
      name: t('cmms.activeWorkOrders'),
      value: (stats.active || 0).toString(),
      change: `${stats.inProgress || 0} in progress`,
      trend: stats.active > 0 ? 'warning' : 'up',
      icon: Wrench,
      color: 'bg-orange-500',
      href: '/portal/cmms/work-orders'
    },
    {
      name: t('cmms.pendingReview'),
      value: (stats.pendingReview || 0).toString(),
      change: t('cmms.awaitingApproval'),
      trend: stats.pendingReview > 0 ? 'warning' : 'up',
      icon: ClipboardCheck,
      color: 'bg-purple-500',
      href: '/portal/cmms/work-orders'
    },
    {
      name: t('cmms.completedToday'),
      value: (stats.completedToday || 0).toString(),
      change: `${stats.completed || 0} total completed`,
      trend: 'up',
      icon: CheckCircle,
      color: 'bg-green-500',
      href: '/portal/cmms/work-orders'
    },
  ];

  return (
    <PermissionGuard permission={PERMISSIONS.VIEW_DASHBOARD}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('cmms.dashboard')}</h1>
            <p className="text-gray-600 mt-1">
              {t('dashboard.welcome')}{cmmsUser?.fullName ? `, ${cmmsUser.fullName}` : ''}
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchDashboardData} className="btn btn-outline">
              <RefreshCw className="w-5 h-5 mr-2" />
              {t('common.refresh')}
            </button>
            {canCreateWorkOrders && (
              <Link to="/portal/cmms/work-orders" className="btn btn-primary">
                <Wrench className="w-5 h-5 mr-2" />
                {t('cmms.workOrders')}
              </Link>
            )}
            {canCreateAssets && (
              <Link to="/portal/cmms/assets" className="btn btn-outline">
                <Package className="w-5 h-5 mr-2" />
                {t('cmms.assets')}
              </Link>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.name}
                to={stat.href}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {stat.trend === 'up' && <TrendingUp className="w-5 h-5 text-green-500" />}
                  {stat.trend === 'warning' && <AlertTriangle className="w-5 h-5 text-orange-500" />}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600 mb-2">{stat.name}</div>
                <div className="text-xs text-gray-500">{stat.change}</div>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('cmms.quickActions')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {canCreateWorkOrders && (
              <Link
                to="/portal/cmms/work-orders"
                className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.workOrders')}</span>
              </Link>
            )}
            {canCreateAssets && (
              <Link
                to="/portal/cmms/assets"
                className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.assets')}</span>
              </Link>
            )}
            <Link
              to="/portal/cmms/pm-schedules"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.pmSchedules')}</span>
            </Link>
            <Link
              to="/portal/cmms/locations"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.locations')}</span>
            </Link>
            <Link
              to="/portal/cmms/suppliers"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.suppliers')}</span>
            </Link>
            <Link
              to="/portal/cmms/reports"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-900 text-center">{t('cmms.reports')}</span>
            </Link>
          </div>
        </div>

        {/* Components Due Widget */}
        <ComponentsDueWidget />

        {/* PM Schedules Due Widget */}
        <PMSchedulesDueWidget />

        {/* Pending Review Alert */}
        {pendingReviewOrders.length > 0 && hasPermission(PERMISSIONS.COMPLETE_WORK_ORDERS) && (
          <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="w-6 h-6 text-purple-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900">
                  {pendingReviewOrders.length} {t('cmms.workOrders')} {t('cmms.pendingReview')}
                </h3>
                <p className="text-sm text-purple-700">
                  {t('cmms.pendingReviewDesc')}
                </p>
              </div>
              <Link
                to="/portal/cmms/work-orders"
                className="btn bg-purple-600 text-white hover:bg-purple-700 px-4 py-2 rounded-lg"
              >
                {t('cmms.reviewNow')}
              </Link>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Assets by Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('cmms.assetsByStatus')}</h2>
              <Link to="/portal/cmms/assets" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                {t('common.viewAll')}
              </Link>
            </div>
            <div className="space-y-4">
              {assetsByStatus.map((item) => (
                <div key={item.status}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{t(item.labelKey)}</span>
                    <span className="text-sm text-gray-600">{item.count} {t('cmms.assets').toLowerCase()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${item.color} h-2 rounded-full transition-all`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">{assets.length}</div>
                <div className="text-sm text-gray-600">{t('cmms.totalAssets')}</div>
              </div>
            </div>
          </div>

          {/* Active Work Orders */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('cmms.activeWorkOrders')}</h2>
              <Link to="/portal/cmms/work-orders" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {upcomingWorkOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>{t('cmms.noActiveWorkOrders')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingWorkOrders.map((wo) => (
                  <Link
                    key={wo.id}
                    to={`/portal/cmms/work-orders/${wo.id}`}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow block"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        wo.status === 'PENDING_REVIEW' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {wo.status === 'PENDING_REVIEW' ? (
                          <ClipboardCheck className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Wrench className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-gray-900 truncate">{wo.title}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(wo.priority)}`}>
                            {wo.priority}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(wo.status)}`}>
                            {wo.status?.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">{wo.assetName} • {wo.woNumber}</div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {wo.scheduledStart ? formatDate(wo.scheduledStart) : '-'}
                      </div>
                      <div className="text-xs text-gray-500">{wo.assignedToName || t('cmms.unassigned')}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Work Order Summary */}
        <div className="bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-2xl font-bold mb-6">{t('cmms.workOrderSummary')}</h2>
          <div className="grid md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Package className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{stats.assigned || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.assigned')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Activity className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{stats.inProgress || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.inProgress')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Clock className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{stats.onHold || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.onHold')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <ClipboardCheck className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{stats.pendingReview || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.pendingReview')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{stats.completed || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.completed')}</div>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default CMSSDashboard;
